import { useState, useEffect, useRef } from "react";
import {
  Button,
  Badge,
  Card,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import ClassCardOptions from "./ClassCardOptions";
import moment from "moment";
import {
  ClockIcon,
  CalendarIcon,
  UserTieIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from "../../../../../constants/icons";
import { openLinkInNewTab } from "../../../../../pages/StudentPages/LiveClasses/utils";
import "./styles.scss";
import { isEditOwnAccess } from "../../../../../utils/permissionsParser";
import withSubscriptionCheckButton from "../../../../../components/Common/withSubscriptionCheckButton";
import { useParams } from "react-router-dom";

const PlayRecordingButtonWithSubscriptionCheck = withSubscriptionCheckButton(
  Button,
  "Class Recordings"
);

const JoinClassButtonWithSubscriptionCheck = withSubscriptionCheckButton(
  Button,
  "Live Classes"
);

const LiveClassTabs = ({
  isMobileView,
  liveClassData,
  upcomingClassData,
  completedClassData,

  // Pagination props
  noOfLivePages,
  pageOfLive,
  loadMoreLiveClasses,
  noOfUpcomingPages,
  pageOfUpcoming,
  loadMoreUpcomingClasses,
  noOfCompletedPages,
  pageOfCompleted,
  loadMoreCompletedClasses,

  // Event handlers
  handleStartClass,
  handleEndClass,
  deleteModalhandler,
  editHandler,
  shareModalHandler,
  onSuspendHandler,
  onRescheduleHandler,
  playRecordings,

  // User and permissions
  userId,
  permissions,
  isStudent,
  showThreeDots,
  completedCount,
  upcomingCount,
}) => {
  const queryParams = new URLSearchParams(window.location.search);
  const isNotifyConfig = queryParams.get("notificationConfig");
  const showCompleted = queryParams.get("showCompleted");
  const { id: classroomId } = useParams();

  const getInitialSecondaryTab = () => {
    if (isNotifyConfig === "true") {
      return "upcoming";
    }
    if (showCompleted === "true") {
      return "completed";
    }
    return "upcoming";
  };

  // Persist secondaryTab in sessionStorage to survive remounts
  const getTabFromStorage = () => {
    return getInitialSecondaryTab();
  };
  const [secondaryTab, setSecondaryTab] = useState(getTabFromStorage);

  useEffect(() => {
    sessionStorage.setItem("liveClassSecondaryTab", secondaryTab);
  }, [secondaryTab]);
  const isLoading = useRef(false);

  // Add new state for slider
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);

  // Calculate number of visible cards based on screen size
  const getVisibleCards = () => {
    if (window.innerWidth >= 768) return 2; // tablet
    return 1; // mobile
  };

  // Handle slider navigation
  const handlePrevSlide = () => {
    setCurrentSlide((prev) => Math.max(0, prev - 1));
  };

  const handleNextSlide = () => {
    const visibleCards = getVisibleCards();
    const maxSlides = Math.ceil(liveClassData.length / visibleCards) - 1;
    setCurrentSlide((prev) => Math.min(maxSlides, prev + 1));
  };

  // Reset slide when switching to mobile view
  useEffect(() => {
    if (isMobileView) {
      setCurrentSlide(0);
    }
  }, [isMobileView]);

  // Scroll position preservation
  useEffect(() => {
    // Immediately check for and restore saved scroll position
    const savedScroll = sessionStorage.getItem("liveClassTabs-scroll");
    if (savedScroll && parseFloat(savedScroll) > 0) {
      setTimeout(() => {
        window.scrollTo(0, parseFloat(savedScroll));
        sessionStorage.removeItem("liveClassTabs-scroll");
      }, 50);
    }

    // Save scroll position on every change (in case of unexpected unmount)
    const globalScrollListener = () => {
      const currentScroll = window.scrollY;
      if (currentScroll > 50) {
        // Only save if user has scrolled significantly
        sessionStorage.setItem(
          "liveClassTabs-scroll",
          currentScroll.toString()
        );
      }
    };

    window.addEventListener("scroll", globalScrollListener, { passive: true });

    return () => {
      // Always save scroll position before unmounting
      const currentScroll = window.scrollY;
      if (currentScroll > 50) {
        sessionStorage.setItem(
          "liveClassTabs-scroll",
          currentScroll.toString()
        );
      }
      window.removeEventListener("scroll", globalScrollListener);
    };
  }, []);

  // The loading states are passed as props from the parent component
  // We don't have direct access to them here, so we'll remove these useEffects
  // The main tracking will be through the global scroll listener above

  // Helper function to get the date field from class item
  const getTimeField = (classItem) => {
    return (
      classItem.startDateTime || classItem.startTime || classItem.start || null
    );
  };

  // Format the date in a better readable format using moment
  const formatDate = (classItem) => {
    try {
      const dateField = getTimeField(classItem);
      if (!dateField) return "N/A";

      return moment(dateField).format("MMM D, YYYY [at] h:mm A");
    } catch (e) {
      console.error("Error formatting date:", e);
      return "N/A";
    }
  };

  // Helper function to get subject name
  const getSubjectName = (classItem) => {
    if (!classItem) return "General";

    // Check for arrays of subjects/categories first
    if (Array.isArray(classItem.subjectNames)) {
      return (
        classItem.subjectNames
          .map((subj) => (typeof subj === "string" ? subj : subj.name || ""))
          .filter(Boolean)
          .join(", ") || "General"
      );
    }

    if (Array.isArray(classItem.subjects)) {
      return (
        classItem.subjects
          .map((subj) => (typeof subj === "string" ? subj : subj.name || ""))
          .filter(Boolean)
          .join(", ") || "General"
      );
    }

    if (Array.isArray(classItem.categories)) {
      return (
        classItem.categories
          .map((cat) => (typeof cat === "string" ? cat : cat.name || ""))
          .filter(Boolean)
          .join(", ") || "General"
      );
    }

    // Check for category as array
    if (classItem.category && Array.isArray(classItem.category)) {
      return (
        classItem.category
          .map((cat) => (typeof cat === "string" ? cat : cat.name || ""))
          .filter(Boolean)
          .join(", ") || "General"
      );
    }

    // Deep property access with fallbacks for single subject/category
    const subjectName =
      classItem.subjectName ||
      (classItem.subject &&
        (typeof classItem.subject === "string"
          ? classItem.subject
          : classItem.subject.name)) ||
      classItem.courseName ||
      (classItem.category &&
        !Array.isArray(classItem.category) &&
        (typeof classItem.category === "string"
          ? classItem.category
          : classItem.category.name)) ||
      classItem.categoryName ||
      (classItem.classCategory &&
        (typeof classItem.classCategory === "string"
          ? classItem.classCategory
          : classItem.classCategory.name));

    // If we couldn't find any subject info, return General
    return subjectName || "General";
  };

  // Helper function to get subject tags array
  const getSubjectTags = (classItem) => {
    if (Array.isArray(classItem.subjectNames)) {
      return classItem.subjectNames;
    }

    if (Array.isArray(classItem.subjects)) {
      return classItem.subjects
        .map((s) => (typeof s === "string" ? s : s.name || ""))
        .filter(Boolean);
    }

    const subjectName = getSubjectName(classItem);
    return subjectName !== "General" ? [subjectName] : [];
  };

  // Helper function to get class title
  const getClassTitle = (classItem) => {
    return (
      classItem.title ||
      classItem.name ||
      classItem.className ||
      classItem.description ||
      "Untitled Class"
    );
  };

  // Get instructor name
  const getInstructor = (classItem) => {
    return (
      classItem.instructor ||
      classItem.teacherName ||
      classItem.teacher?.name ||
      null
    );
  };

  // Get duration in minutes
  const getDuration = (classItem) => {
    return classItem.duration || 60;
  };

  // Get actual Duration from sessions
  const getSessionDuration = (classItem) => {
    if (!classItem.sessions || classItem.sessions.length === 0) return null;
    let totalMinutes = 0;
    classItem.sessions.forEach((session) => {
      if (session.startDateTime && session.endDateTime) {
        const diff = moment(session.endDateTime).diff(moment(session.startDateTime), "minutes");
        if (diff > 0) totalMinutes += diff;
      }
    });
    return totalMinutes > 0 ? totalMinutes : null;
  };

  // Format session duration for display
  const formatSessionDuration = (minutes) => {
    if (!minutes) return null;
    if (minutes < 60) return `${minutes} min`;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
  };

  // Get menu options based on type
  const getMenuOptions = (type, classItem) => {
    // Immediately return empty array for students
    if (isStudent) {
      return [];
    }

    const upcomingMenuList = [
      {
        text: "Edit",
        actionsHandler: (id) => editHandler(id),
        imgSrc: "/editIcon.svg",
        imgAltText: "edit-icon",
      },
      {
        text: "Delete",
        actionsHandler: (id) => deleteModalhandler(id),
        imgSrc: "/deleteIcon.svg",
        imgAltText: "delete-icon",
      },
      {
        text: "Share",
        actionsHandler: (id) => shareModalHandler(id),
        imgSrc: "/Share.svg",
        imgAltText: "share-icon",
      },
      {
        text: (row) => (row?.isSuspended ? "Resume" : "Suspend"),
        actionsHandler: (id) => onSuspendHandler(id),
        imgSrc: "/Share.svg",
        imgAltText: "resumeOrSuspend",
      },
      {
        text: "Reschedule",
        actionsHandler: (id) => onRescheduleHandler(id),
        imgSrc: "/Share.svg",
        imgAltText: "share-icon",
      },
    ];

    const liveMenuList = [
      {
        text: "Share",
        actionsHandler: (id) => shareModalHandler(id),
        imgSrc: "/Share.svg",
        imgAltText: "share-icon",
      },
    ];

    const completedMenuList = [
      {
        text: "Edit",
        actionsHandler: (id) => editHandler(id),
        imgSrc: "/editIcon.svg",
        imgAltText: "edit-icon",
      },
      {
        text: "Delete",
        actionsHandler: (id) => deleteModalhandler(id),
        imgSrc: "/deleteIcon.svg",
        imgAltText: "delete-icon",
      },
      {
        text: "View Attendance",
        actionsHandler: (id, attendanceUrl) => {
          openLinkInNewTab(attendanceUrl);
        },
        imgSrc: "/attendanceIcon.svg",
        imgAltText: "attendance-icon",
      },
    ];

    const getValidOptionsList = (optionsList, createdBy) => {
      let options = [...optionsList];

      // Check for edit permissions
      const canEdit =
        permissions?.edit || (permissions?.edit_own && userId === createdBy);

      if (!canEdit) {
        options = options.filter(
          (option) =>
            option?.text !== "Edit" &&
            option?.imgAltText !== "resumeOrSuspend" &&
            option?.text !== "Reschedule"
        );
      }

      // Check for delete permissions
      const canDelete =
        permissions?.delete ||
        (permissions?.delete_own && userId === createdBy);
      if (!canDelete) {
        options = options.filter((option) => option?.text !== "Delete");
      }

      return options;
    };

    switch (type) {
      case "live":
        return getValidOptionsList(liveMenuList, classItem.createdBy);
      case "upcoming":
        return getValidOptionsList(
          classItem?.isSuspended
            ? upcomingMenuList?.filter(
                (menuItem) =>
                  menuItem?.imgAltText === "resumeOrSuspend" ||
                  menuItem?.text === "Delete"
              )
            : classItem?.rescheduledAt
            ? []
            : upcomingMenuList,
          classItem.createdBy
        );
      case "completed":
        return getValidOptionsList(
          classItem?.attendanceUrl ? completedMenuList : [completedMenuList[0], completedMenuList[1]],
          classItem.createdBy
        );
      default:
        return [];
    }
  };

  // Function to render a class row item for the list view
  const renderClassListItem = (classItem, type, index) => {
    const formattedDate = formatDate(classItem);
    const classTitle = getClassTitle(classItem);
    const duration = getDuration(classItem);
    const instructor = getInstructor(classItem);
    const subjectTags = getSubjectTags(classItem);
    const sessionDuration = type === "completed" ? formatSessionDuration(getSessionDuration(classItem)) : null;
    const statusBadge = getStatusBadge(type, classItem);
    const menuOptions = getMenuOptions(type, classItem);

    // Check if Edit option exists in the menuOptions
    const hasEditOption = menuOptions.some(
      (option) => typeof option.text === "string" && option.text === "Edit"
    );

    // Check if class is rescheduled or suspended
    const isRescheduled = !!classItem?.rescheduledAt;
    const isSuspended = !!classItem?.isSuspended;

    // Check permissions
    const canEdit =
      permissions?.edit ||
      isEditOwnAccess(permissions, userId, classItem.createdBy);
    const canShowActions = canEdit && !isSuspended && !isRescheduled;

    if (isMobileView) {
      return (
        <Card
          className={`class-list-item-mobile ${type}-item mb-3 border-0 shadow-sm ${
            isSuspended ? "suspended" : ""
          }`}
          key={`${type}-list-${classItem._id}-${index}`}
        >
          <Card.Body className="p-3">
            <div className="title-container">
              {statusBadge}
              <div className="title-wrapper">
                <h6 className="class-title">{classTitle}</h6>
                {!isStudent && showThreeDots && menuOptions.length > 0 && (
                  <div className="options-menu">
                    <ClassCardOptions
                      options={menuOptions}
                      classId={classItem?._id}
                      classData={classItem}
                      additionalParam={classItem?.attendanceUrl}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="class-details-mobile">
              <div className="detail-row">
                <CalendarIcon className="detail-icon text-primary" />
                <span className="ms-2 small">{formattedDate}</span>
              </div>

              {instructor && (
                <div className="detail-row">
                  <UserTieIcon className="detail-icon text-primary" />
                  <span className="ms-2 small">{instructor}</span>
                </div>
              )}

              {type === "completed" && sessionDuration ? (
                <div className="detail-row">
                  <ClockIcon className="detail-icon text-success" />
                  <span className="ms-2 small text-success fw-semibold">
                    Duration: {sessionDuration}
                  </span>
                </div>
              ) : (
                <div className="detail-row">
                  <ClockIcon className="detail-icon text-primary" />
                  <span className="ms-2 small">{duration} minutes</span>
                </div>
              )}

              {subjectTags.length > 0 && (
                <div className="subject-tags-mobile mt-2">
                  {subjectTags.slice(0, 2).map((subject, idx) => (
                    <Badge
                      key={idx}
                      bg="light"
                      text="dark"
                      className="me-1 subject-badge"
                    >
                      {subject}
                    </Badge>
                  ))}
                  {subjectTags.length > 2 && (
                    <Badge bg="light" text="dark" className="subject-badge">
                      +{subjectTags.length - 2}
                    </Badge>
                  )}
                </div>
              )}
            </div>

            <div className="action-buttons-mobile mt-3">
              {type === "live" && (
                <>
                  {isStudent ? (
                    <JoinClassButtonWithSubscriptionCheck
                      variant="primary"
                      size="sm"
                      className="w-100"
                      style={{ width: "100%" }}
                      onClick={() =>
                        handleStartClass(
                          classItem._id,
                          classItem.meetingID,
                          isStudent
                            ? classItem.attendeePW
                            : classItem.moderatorPW,
                          isStudent ? "join" : "start"
                        )
                      }
                      classRoomId={classroomId}
                      subjects={classItem.subjects}
                    >
                      <i className="fas fa-sign-in-alt me-1"></i> Join Class
                    </JoinClassButtonWithSubscriptionCheck>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-100"
                      onClick={() =>
                        handleStartClass(
                          classItem._id,
                          classItem.meetingID,
                          isStudent
                            ? classItem.attendeePW
                            : classItem.moderatorPW,
                          isStudent ? "join" : "start"
                        )
                      }
                    >
                      <i className="fas fa-sign-in-alt me-1"></i> Join Class
                    </Button>
                  )}
                </>
              )}
              {type === "upcoming" && canShowActions && !isStudent && (
                <div className="d-grid gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() =>
                      handleStartClass(
                        classItem._id,
                        classItem.meetingID,
                        isStudent
                          ? classItem.attendeePW
                          : classItem.moderatorPW,
                        isStudent ? "join" : "start"
                      )
                    }
                  >
                    <i className="fas fa-play me-1"></i> Start
                  </Button>
                  {!hasEditOption && canEdit && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => editHandler(classItem)}
                    >
                      <i className="fas fa-edit me-1"></i> Edit
                    </Button>
                  )}
                </div>
              )}
              {type === "completed" && (
                <div className="d-grid gap-2">
                  {classItem.hasRecordingFile && isStudent && (
                    <PlayRecordingButtonWithSubscriptionCheck
                      variant="success"
                      size="sm"
                      className="w-100"
                      style={{ width: "100%" }}
                      onClick={() => playRecordings(classItem._id)}
                      classRoomId={classroomId}
                      subjects={classItem.subjects}
                    >
                      Play Recording
                    </PlayRecordingButtonWithSubscriptionCheck>
                  )}
                  {classItem.hasRecordingFile && !isStudent && (
                    <Button
                      variant="success"
                      size="sm"
                      className="w-100"
                      onClick={() => playRecordings(classItem._id)}
                    >
                      Play Recording
                    </Button>
                  )}
                  {!isStudent && canShowActions && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="w-100"
                      onClick={() =>
                        handleStartClass(
                          classItem._id,
                          classItem.meetingID,
                          isStudent
                            ? classItem.attendeePW
                            : classItem.moderatorPW,
                          isStudent ? "join" : "start"
                        )
                      }
                    >
                      <i className="fas fa-play me-1"></i> Start
                    </Button>
                  )}
                </div>
              )}
            </div>

            {classItem?.rescheduledAt && (
              <div className="rescheduled-badge mt-2">
                <Badge
                  pill
                  style={{
                    backgroundColor: "rgb(23, 162, 184)",
                    color: "white",
                  }}
                >
                  Rescheduled to{" "}
                  {moment(classItem?.rescheduledAt).format(
                    "MMM D, YYYY, h:mm A"
                  )}
                </Badge>
              </div>
            )}
          </Card.Body>
        </Card>
      );
    }

    // Return the original desktop view
    return (
      <Card
        className={`class-list-item ${type}-item mb-3 border-0 shadow-sm ${
          isSuspended ? "suspended" : ""
        }`}
        key={`${type}-list-${classItem._id}-${index}`}
      >
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start">
            <div className="class-info flex-grow-1">
              <div className="d-flex align-items-center mb-2 justify-content-between">
                <div className="d-flex align-items-center">
                  {classTitle.length > 40 ? (
                    <OverlayTrigger
                      overlay={(props) => (
                        <Tooltip {...props}>{classTitle}</Tooltip>
                      )}
                      placement="top"
                    >
                      <h5 className="class-title mb-0 me-2">{`${classTitle.slice(
                        0,
                        37
                      )}...`}</h5>
                    </OverlayTrigger>
                  ) : (
                    <h5 className="class-title mb-0 me-2">{classTitle}</h5>
                  )}
                  {statusBadge}
                </div>

                {/* Options menu - Only show if not student and showThreeDots is true */}
                {!isStudent &&
                  showThreeDots &&
                  getMenuOptions(type, classItem).length > 0 && (
                    <div className="options-menu">
                      <ClassCardOptions
                        options={getMenuOptions(type, classItem)}
                        classId={classItem?._id}
                        classData={classItem}
                        additionalParam={classItem?.attendanceUrl}
                      />
                    </div>
                  )}
              </div>

              {/* Subject tags */}
              <div className="subject-tags mb-3 d-flex justify-content-start">
                {subjectTags.map((subject, idx) => (
                  <Badge
                    key={idx}
                    bg="light"
                    text="dark"
                    className="me-1 mb-1 subject-badge"
                  >
                    {subject}
                  </Badge>
                ))}
              </div>

              <Row className="class-details g-3">
                <Col xs={12} md={6} className="pe-md-4">
                  <div className="detail-group">
                    <div className="detail-item">
                      <CalendarIcon className="detail-icon text-primary" />
                      <span className="ms-2">{formattedDate}</span>
                    </div>

                    {instructor && (
                      <div className="detail-item mt-2">
                        <UserTieIcon className="detail-icon text-primary" />
                        <span className="ms-2">{instructor}</span>
                      </div>
                    )}

                    {type === "completed" && sessionDuration ? (
                      <div className="detail-item mt-2">
                        <ClockIcon className="detail-icon text-success" />
                        <span className="ms-2 text-success fw-semibold">
                          Duration: {sessionDuration}
                        </span>
                      </div>
                    ) : (
                      <div className="detail-item mt-2">
                        <ClockIcon className="detail-icon text-primary" />
                        <span className="ms-2">{duration} minutes</span>
                      </div>
                    )}
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div className="action-area d-flex flex-column align-items-md-end">
                    <div className="d-flex justify-content-md-end">
                      {type === "live" && (
                        <div className="action-buttons">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() =>
                              handleStartClass(
                                classItem._id,
                                classItem.meetingID,
                                isStudent
                                  ? classItem.attendeePW
                                  : classItem.moderatorPW,
                                isStudent ? "join" : "start"
                              )
                            }
                          >
                            <i className="fas fa-sign-in-alt me-1"></i> Join
                            Class
                          </Button>
                        </div>
                      )}
                      {type === "upcoming" && canShowActions && !isStudent && (
                        <div className="action-buttons">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() =>
                              handleStartClass(
                                classItem._id,
                                classItem.meetingID,
                                isStudent
                                  ? classItem.attendeePW
                                  : classItem.moderatorPW,
                                isStudent ? "join" : "start"
                              )
                            }
                          >
                            <i className="fas fa-play me-1"></i> Start
                          </Button>
                          {!hasEditOption && canEdit && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => editHandler(classItem)}
                            >
                              <i className="fas fa-edit me-1"></i> Edit
                            </Button>
                          )}
                        </div>
                      )}
                      {type === "completed" && (
                        <div className="action-buttons">
                          {classItem.hasRecordingFile && (
                            <>
                              {isStudent ? (
                                <PlayRecordingButtonWithSubscriptionCheck
                                  variant="success"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => playRecordings(classItem._id)}
                                  classRoomId={classroomId}
                                  subjects={classItem.subjects}
                                >
                                  Play Recording
                                </PlayRecordingButtonWithSubscriptionCheck>
                              ) : (
                                <Button
                                  variant="success"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => playRecordings(classItem._id)}
                                >
                                  Play Recording
                                </Button>
                              )}
                            </>
                          )}
                          {!isStudent && canShowActions && (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="me-2"
                              onClick={() =>
                                handleStartClass(
                                  classItem._id,
                                  classItem.meetingID,
                                  isStudent
                                    ? classItem.attendeePW
                                    : classItem.moderatorPW,
                                  isStudent ? "join" : "start"
                                )
                              }
                            >
                              <i className="fas fa-play me-1"></i> Start
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Status indicators */}
              {classItem?.rescheduledAt && (
                <div className="rescheduled-badge mt-2">
                  <Badge
                    pill
                    style={{
                      backgroundColor: "rgb(23, 162, 184)",
                      color: "white",
                    }}
                  >
                    Rescheduled to{" "}
                    {moment(classItem?.rescheduledAt).format(
                      "MMM D, YYYY, h:mm A"
                    )}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Function to get status badge type based on class type
  const getStatusBadge = (type) => {
    let bg = "secondary";
    let text = "";
    let customStyle = {};

    switch (type) {
      case "live":
        bg = "danger";
        text = "LIVE";
        break;
      case "upcoming":
        bg = "warning";
        text = "UPCOMING";
        customStyle = {
          backgroundColor: "#ffc107",
          color: "#000",
          fontWeight: "500",
        };
        break;
      case "completed":
        bg = "success";
        text = "COMPLETED";
        break;
      default:
        text = "";
    }

    return (
      <Badge bg={bg} className="status-badge" style={customStyle}>
        {text}
      </Badge>
    );
  };

  return (
    <div className="modern-class-container">
      {/* Live Classes Section - Always Visible on Top */}
      <section className="live-classes-section mb-5">
        <div className="section-header d-flex justify-content-between align-items-center mb-4">
          <h3 className="section-title">
            Live Now
            {liveClassData.length > 0 && (
              <Badge bg="danger" pill className="ms-2 pulse-animation">
                {liveClassData.length}
              </Badge>
            )}
          </h3>
        </div>

        {liveClassData.length > 0 ? (
          <div
            className={`${
              isMobileView ? "live-classes-slider" : "live-classes-grid"
            } position-relative`}
          >
            <div className={isMobileView ? "slider-container" : ""}>
              <Row className="g-4" ref={sliderRef}>
                {liveClassData.map((classItem, index) => {
                  const classTitle = getClassTitle(classItem);
                  const startTime = formatDate(classItem);

                  return (
                    <Col
                      key={`live-card-${classItem._id}-${index}`}
                      xs={12}
                      md={6}
                      lg={4}
                      xl={3}
                      xxl={3}
                      style={
                        isMobileView
                          ? {
                              transform: `translateX(-${currentSlide * 100}%)`,
                              transition: "transform 0.3s ease-in-out",
                              width: window.innerWidth >= 768 ? "50%" : "100%",
                              flex: "0 0 auto",
                            }
                          : {}
                      }
                    >
                      <Card className="live-class-card h-100 shadow-sm border-0">
                        <Card.Body className="d-flex flex-column">
                          <div className="d-flex justify-content-between">
                            <div className="live-indicator mb-2">
                              <span className="badge bg-danger pulse-animation">
                                LIVE
                              </span>
                            </div>
                            {/* Only show options menu if not student and showThreeDots is true */}
                            {!isStudent &&
                              showThreeDots &&
                              getMenuOptions("live", classItem).length > 0 && (
                                <div className="options-menu">
                                  <ClassCardOptions
                                    options={getMenuOptions("live", classItem)}
                                    classId={classItem?._id}
                                    classData={classItem}
                                  />
                                </div>
                              )}
                          </div>

                          <Card.Title className="text-truncate">
                            {classTitle}
                          </Card.Title>

                          <div className="class-time-info d-flex align-items-center mb-2">
                            <CalendarIcon
                              className="text-primary me-2"
                              style={{ width: "18px" }}
                            />
                            <span className="small">{startTime}</span>
                          </div>

                          {getSubjectTags(classItem).length > 0 && (
                            <div className="subject-tags mb-3 flex-grow-1">
                              {getSubjectTags(classItem)
                                .slice(0, 3)
                                .map((subject, idx) => (
                                  <Badge
                                    key={idx}
                                    bg="light"
                                    text="dark"
                                    className="me-1 mb-1 subject-badge"
                                  >
                                    {subject}
                                  </Badge>
                                ))}
                              {getSubjectTags(classItem).length > 3 && (
                                <Badge
                                  bg="light"
                                  text="dark"
                                  className="subject-badge"
                                >
                                  +{getSubjectTags(classItem).length - 3}
                                </Badge>
                              )}
                            </div>
                          )}

                          {/* Show join button for students, show both join and end buttons for teachers */}
                          <div className="d-grid gap-2 mt-auto">
                            {isStudent ? (
                              <JoinClassButtonWithSubscriptionCheck
                                variant="primary"
                                className="join-btn"
                                style={{ width: "100%" }}
                                onClick={() =>
                                  handleStartClass(
                                    classItem._id,
                                    classItem.meetingID,
                                    isStudent
                                      ? classItem.attendeePW
                                      : classItem.moderatorPW,
                                    isStudent ? "join" : "start"
                                  )
                                }
                                classRoomId={classroomId}
                                subjects={classItem.subjects}
                              >
                                <i className="fas fa-sign-in-alt me-1"></i> Join
                                Class
                              </JoinClassButtonWithSubscriptionCheck>
                            ) : (
                              <Button
                                variant="primary"
                                className="join-btn"
                                onClick={() =>
                                  handleStartClass(
                                    classItem._id,
                                    classItem.meetingID,
                                    isStudent
                                      ? classItem.attendeePW
                                      : classItem.moderatorPW,
                                    isStudent ? "join" : "start"
                                  )
                                }
                              >
                                <i className="fas fa-sign-in-alt me-1"></i> Join
                                Class
                              </Button>
                            )}
                            {!isStudent && permissions?.edit && (
                              <Button
                                variant="outline-danger"
                                className="end-btn"
                                onClick={async () => {
                                  const success = await handleEndClass(
                                    classItem._id,
                                    classItem.meetingID,
                                    classItem.moderatorPW
                                  );
                                  if (!success) {
                                    // The error message is already shown by the hook
                                    return;
                                  }
                                }}
                              >
                                <i className="fas fa-times-circle me-1"></i> End
                                Class
                              </Button>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </div>

            {/* Navigation Arrows - Only show on mobile view */}
            {isMobileView && currentSlide > 0 && (
              <Button
                variant="light"
                className="slider-arrow slider-arrow-left"
                onClick={handlePrevSlide}
              >
                <ArrowLeftIcon className="text-primary" />
              </Button>
            )}
            {isMobileView &&
              currentSlide <
                Math.ceil(liveClassData.length / getVisibleCards()) - 1 && (
                <Button
                  variant="light"
                  className="slider-arrow slider-arrow-right"
                  onClick={handleNextSlide}
                >
                  <ArrowRightIcon className="text-primary" />
                </Button>
              )}
          </div>
        ) : (
          <div className="no-live-classes">
            <Card className="text-center border-0 shadow-sm">
              <Card.Body className="p-5">
                <i
                  className="fas fa-video-slash text-muted mb-3"
                  style={{ fontSize: "2.5rem" }}
                ></i>
                <Card.Title>No Live Classes</Card.Title>
                <Card.Text className="text-muted">
                  There are no live classes in progress at the moment
                </Card.Text>
              </Card.Body>
            </Card>
          </div>
        )}

        {noOfLivePages > pageOfLive && (
          <div className="text-center mt-4">
            <Button
              variant="outline-primary"
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isLoading.current) return;

                isLoading.current = true;
                try {
                  await loadMoreLiveClasses();
                } finally {
                  isLoading.current = false;
                }
              }}
              className="load-more-button"
              disabled={isLoading.current}
            >
              {isLoading.current ? "Loading..." : "Load More Live Classes"}
            </Button>
          </div>
        )}
      </section>

      {/* Secondary Tabs Section for Upcoming/Completed Classes */}
      <section className="secondary-classes-section">
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white pb-0">
            <div className="custom-tabs-container">
              <div
                className={`custom-tab ${
                  secondaryTab === "upcoming" ? "active" : ""
                }`}
                onClick={() => setSecondaryTab("upcoming")}
              >
                <span className="tab-label">Upcoming Classes</span>
                {upcomingCount > 0 && (
                  <Badge bg="info" pill className="ms-2">
                    {upcomingCount}
                  </Badge>
                )}
              </div>
              <div
                className={`custom-tab ${
                  secondaryTab === "completed" ? "active" : ""
                }`}
                onClick={() => setSecondaryTab("completed")}
              >
                <span className="tab-label">Completed Classes</span>
                {completedCount > 0 && (
                  <Badge bg="secondary" pill className="ms-2">
                    {completedCount}
                  </Badge>
                )}
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-3">
            {secondaryTab === "upcoming" && (
              <div className="upcoming-classes-list">
                {upcomingClassData.length > 0 ? (
                  <>
                    <div className="list-body">
                      {upcomingClassData.map((classItem, index) =>
                        renderClassListItem(classItem, "upcoming", index)
                      )}
                    </div>
                    {noOfUpcomingPages > pageOfUpcoming && (
                      <div className="text-center mt-4">
                        <Button
                          variant="outline-primary"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isLoading.current) return;

                            isLoading.current = true;
                            try {
                              await loadMoreUpcomingClasses();
                            } finally {
                              isLoading.current = false;
                            }
                          }}
                          className="load-more-button"
                          disabled={isLoading.current}
                        >
                          {isLoading.current
                            ? "Loading..."
                            : "Load More Upcoming Classes"}
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-5">
                    <i
                      className="far fa-calendar-alt text-muted mb-3"
                      style={{ fontSize: "2.5rem" }}
                    ></i>
                    <h5>No Upcoming Classes</h5>
                    <p className="text-muted">
                      You have no classes scheduled for the future
                    </p>
                  </div>
                )}
              </div>
            )}

            {secondaryTab === "completed" && (
              <div className="completed-classes-list">
                {completedClassData.length > 0 ? (
                  <>
                    <div className="list-body">
                      {completedClassData.map((classItem, index) =>
                        renderClassListItem(classItem, "completed", index)
                      )}
                    </div>
                    {noOfCompletedPages > pageOfCompleted && (
                      <div className="text-center mt-4">
                        <Button
                          variant="outline-secondary"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isLoading.current) return;

                            isLoading.current = true;
                            try {
                              await loadMoreCompletedClasses();
                            } finally {
                              isLoading.current = false;
                            }
                          }}
                          className="load-more-button"
                          disabled={isLoading.current}
                        >
                          {isLoading.current
                            ? "Loading..."
                            : "Load More Completed Classes"}
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-5">
                    <i
                      className="fas fa-history text-muted mb-3"
                      style={{ fontSize: "2.5rem" }}
                    ></i>
                    <h5>No Completed Classes</h5>
                    <p className="text-muted">
                      You haven't completed any classes yet
                    </p>
                  </div>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      </section>
    </div>
  );
};

export default LiveClassTabs;
